// Forum post detail with threaded replies.
//
// Layout:
//   • Title + category badge + author/time on top
//   • Vote column + markdown-rendered body
//   • Owner/admin "Edit / Delete" menu
//   • Reply composer at the bottom
//   • Threaded reply list (2 levels deep — top-level + their children)
//
// All state is kept local — we re-fetch the post on every mutation to keep
// counters in sync (cheap because each post only has its own replies).
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Trash2, Edit3, Flag, MessageCircle, MoreVertical, X, CheckCircle2, Award } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import VoteButtons from "@/components/forum/VoteButtons";
import MarkdownEditor from "@/components/forum/MarkdownEditor";
import ReputationBadge from "@/components/forum/ReputationBadge";
import { metaFor, timeAgo } from "@/lib/forumCategories";
import { useAuth } from "@/context/AuthContext";

// Render markdown with custom paragraph handler that turns @mentions into
// styled chips. We don't deep-link the mention because we don't have a
// public user profile yet — just visual highlight + tooltip.
const MENTION_RE = /(^|\s)@([A-Za-z0-9_]{2,40})\b/g;
function mentionify(text) {
  // Returns an array of React fragments — text chunks + <span> chips.
  const out = [];
  let last = 0;
  let m;
  let key = 0;
  while ((m = MENTION_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index + m[1].length));
    out.push(
      <span
        key={`mn-${key++}`}
        className="inline-block px-1 rounded bg-emerald-100 text-emerald-800 font-semibold"
        title="Forum mention"
      >
        @{m[2]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// react-markdown component overrides — wrap each text node through
// mentionify so @user references are styled inside any block.
const MD_COMPONENTS = {
  p: ({ children }) => <p>{transformChildren(children)}</p>,
  li: ({ children }) => <li>{transformChildren(children)}</li>,
};
function transformChildren(children) {
  if (typeof children === "string") return mentionify(children);
  if (Array.isArray(children)) {
    return children.map((c, i) =>
      typeof c === "string"
        ? <span key={i}>{mentionify(c)}</span>
        : c,
    );
  }
  return children;
}

function AuthorChip({ author, when }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
      <span className={"w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white " + (author?.is_system ? "bg-amber-500" : "bg-zinc-700")}>
        {(author?.display_name || "?")[0].toUpperCase()}
      </span>
      <span className="font-medium text-zinc-700">{author?.display_name || "anon"}</span>
      {author?.is_system && (
        <span className="text-amber-600 text-[10px] font-semibold uppercase">community</span>
      )}
      <ReputationBadge reputation={author?.reputation} compact />
      <span className="text-zinc-400">· {timeAgo(when)}</span>
    </span>
  );
}

function ReplyComposer({ postId, parentReplyId, onPosted, onCancel, autoFocus }) {
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (body.trim().length < 2 || saving) return;
    setSaving(true);
    try {
      await api.post(`/forum/posts/${postId}/replies`, {
        body: body.trim(),
        parent_reply_id: parentReplyId || undefined,
      });
      setBody("");
      onPosted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't post reply");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className={parentReplyId ? "ml-8 mt-3" : "mt-4"} data-testid={parentReplyId ? `reply-composer-${parentReplyId}` : "reply-composer-root"}>
      <MarkdownEditor
        value={body}
        onChange={setBody}
        placeholder={parentReplyId ? "Reply to this comment…" : "Add your reply…"}
        rows={4}
        testIdPrefix={parentReplyId ? `reply-editor-${parentReplyId}` : "reply-editor-root"}
      />
      <div className="flex items-center gap-2 justify-end mt-2">
        {onCancel && (
          <Button variant="outline" size="sm" onClick={onCancel} data-testid="reply-cancel-btn">
            <X size={12} className="mr-1" /> Cancel
          </Button>
        )}
        <Button
          size="sm"
          onClick={submit}
          disabled={body.trim().length < 2 || saving}
          data-testid={parentReplyId ? `reply-submit-btn-${parentReplyId}` : "reply-submit-btn-root"}
        >
          {saving ? "Posting…" : "Post reply"}
        </Button>
      </div>
    </div>
  );
}

function ReplyItem({ reply, postId, currentUser, isPostAuthor, isBestAnswer, onChanged, children }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(reply.body);
  const [replyingTo, setReplyingTo] = useState(false);
  const isOwner = reply.author?.id === currentUser?.id;
  const isAdmin = currentUser?.role === "admin";
  const canModify = isOwner || isAdmin;
  const canReply = (reply.depth || 0) < 1; // 2-level cap
  // OP can mark any reply except their own as best answer.
  const canMarkBest = isPostAuthor && !isOwner;

  const saveEdit = async () => {
    try {
      await api.patch(`/forum/replies/${reply.id}`, { body: editValue.trim() });
      setEditing(false);
      onChanged?.();
      toast.success("Reply updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    }
  };
  const remove = async () => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/forum/replies/${reply.id}`);
      onChanged?.();
      toast.success("Reply deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    }
  };
  const markBest = async () => {
    try {
      await api.post(`/forum/posts/${postId}/best-answer`, { reply_id: reply.id });
      onChanged?.();
      toast.success("Marked as the best answer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't mark");
    }
  };
  const unmarkBest = async () => {
    try {
      await api.delete(`/forum/posts/${postId}/best-answer`);
      onChanged?.();
      toast.success("Best answer cleared");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't clear");
    }
  };

  return (
    <div
      id={`reply-${reply.id}`}
      className={
        "py-3 " +
        ((reply.depth || 0) > 0 ? "ml-8 border-l-2 border-zinc-100 pl-4 " : "") +
        (isBestAnswer ? "bg-emerald-50/40 border-l-4 border-l-emerald-500 -ml-1 pl-3 rounded-r-md" : "")
      }
      data-testid={`reply-${reply.id}`}
    >
      {isBestAnswer && (
        <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-2 px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-200" data-testid={`best-answer-stamp-${reply.id}`}>
          <CheckCircle2 size={11} /> Best answer
        </div>
      )}
      <div className="flex gap-3">
        <VoteButtons
          targetType="reply"
          targetId={reply.id}
          score={reply.score}
          myVote={reply.my_vote}
          ownPost={isOwner}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <AuthorChip author={reply.author} when={reply.created_at} />
            <div className="flex items-center gap-1">
              {canMarkBest && !isBestAnswer && (
                <button
                  type="button"
                  onClick={markBest}
                  className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-emerald-700 font-semibold px-2 py-1 rounded hover:bg-emerald-50 transition"
                  title="Mark as the best answer"
                  data-testid={`mark-best-btn-${reply.id}`}
                >
                  <Award size={12} /> Best
                </button>
              )}
              {canMarkBest && isBestAnswer && (
                <button
                  type="button"
                  onClick={unmarkBest}
                  className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:text-rose-600 font-semibold px-2 py-1 rounded hover:bg-rose-50 transition"
                  title="Clear best answer"
                  data-testid={`unmark-best-btn-${reply.id}`}
                >
                  <X size={12} /> Unmark
                </button>
              )}
              {canModify && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="p-1 text-zinc-400 hover:text-zinc-700" data-testid={`reply-menu-${reply.id}`}>
                      <MoreVertical size={14} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditing(true)} data-testid={`reply-edit-${reply.id}`}>
                      <Edit3 size={12} className="mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-rose-600" onClick={remove} data-testid={`reply-delete-${reply.id}`}>
                      <Trash2 size={12} className="mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
          {editing ? (
            <div className="mt-2">
              <MarkdownEditor
                value={editValue}
                onChange={setEditValue}
                rows={4}
                testIdPrefix={`reply-edit-editor-${reply.id}`}
              />
              <div className="flex items-center gap-2 justify-end mt-2">
                <Button variant="outline" size="sm" onClick={() => { setEditing(false); setEditValue(reply.body); }}>Cancel</Button>
                <Button size="sm" onClick={saveEdit} data-testid={`reply-save-${reply.id}`}>Save</Button>
              </div>
            </div>
          ) : (
            <div className="prose prose-sm max-w-none mt-1 text-zinc-800" data-testid={`reply-body-${reply.id}`}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{reply.body || ""}</ReactMarkdown>
            </div>
          )}
          {canReply && !editing && (
            <button
              type="button"
              onClick={() => setReplyingTo((v) => !v)}
              className="text-xs text-zinc-500 hover:text-zinc-900 mt-1 font-medium"
              data-testid={`reply-reply-btn-${reply.id}`}
            >
              <MessageCircle size={12} className="inline mr-1" /> Reply
            </button>
          )}
          {replyingTo && (
            <ReplyComposer
              postId={postId}
              parentReplyId={reply.id}
              onPosted={() => { setReplyingTo(false); onChanged?.(); }}
              onCancel={() => setReplyingTo(false)}
              autoFocus
            />
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function ForumPostDetail() {
  const { postId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/forum/posts/${postId}`);
      setPost(data);
      setEditTitle(data.title);
      setEditBody(data.body);
    } catch (err) {
      setError(err?.response?.data?.detail || "Post not found");
      setPost(null);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="px-4 py-6 max-w-4xl mx-auto text-zinc-500" data-testid="forum-detail-loading">Loading…</div>;
  }
  if (error || !post) {
    return (
      <div className="px-4 py-6 max-w-4xl mx-auto text-center" data-testid="forum-detail-error">
        <div className="text-zinc-700 font-medium">{error || "Post not found"}</div>
        <Link to="/app/community/forum" className="text-sm text-zinc-500 underline mt-2 inline-block">← Back to Forum</Link>
      </div>
    );
  }

  const isOwner = post.author?.id === user?.id;
  const isAdmin = user?.role === "admin";
  const canModify = isOwner || isAdmin;
  const meta = metaFor(post.category);
  const Icon = meta.icon;

  // Build a parent-id → children map so we can render nested replies.
  const childrenOf = (parentId) =>
    (post.replies || []).filter((r) => (r.parent_reply_id || null) === parentId);

  const savePostEdit = async () => {
    try {
      await api.patch(`/forum/posts/${postId}`, {
        title: editTitle.trim(), body: editBody.trim(),
      });
      setEditing(false);
      load();
      toast.success("Question updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    }
  };
  const removePost = async () => {
    if (!window.confirm("Delete this question? Replies will be preserved for admins.")) return;
    try {
      await api.delete(`/forum/posts/${postId}`);
      toast.success("Question deleted");
      nav("/app/community/forum");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    }
  };
  const submitReport = async () => {
    if (reportReason.trim().length < 3) return;
    try {
      await api.post(`/forum/posts/${postId}/report`, { reason: reportReason.trim() });
      setReportOpen(false);
      setReportReason("");
      toast.success("Reported. Our team will review.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't report");
    }
  };

  return (
    <div className="px-4 py-6 max-w-4xl mx-auto" data-testid="forum-detail-page">
      <Link to="/app/community/forum" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-3" data-testid="forum-back-link">
        <ArrowLeft size={12} /> Back to Forum
      </Link>

      <article className="bg-white border rounded-lg p-5">
        <div className="flex items-start gap-4">
          <VoteButtons
            targetType="post"
            targetId={post.id}
            score={post.score}
            myVote={post.my_vote}
            ownPost={isOwner}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border"
                style={{ borderColor: meta.accent + "55", color: meta.accent, background: meta.accent + "10" }}
              >
                <Icon size={11} /> {post.category.replace(/-/g, " ")}
              </span>
              {post.is_solved && (
                <span
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 font-bold uppercase tracking-wide"
                  data-testid="post-solved-badge"
                >
                  <CheckCircle2 size={11} /> Solved
                </span>
              )}
              {(post.tags || []).map((t) => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">#{t}</span>
              ))}
              <div className="flex-1" />
              {!canModify && (
                <button
                  type="button"
                  onClick={() => setReportOpen(true)}
                  className="text-xs text-zinc-400 hover:text-rose-600 inline-flex items-center gap-1"
                  data-testid="forum-report-btn"
                >
                  <Flag size={11} /> Report
                </button>
              )}
              {canModify && !editing && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="p-1 text-zinc-400 hover:text-zinc-700" data-testid="post-menu-btn">
                      <MoreVertical size={14} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditing(true)} data-testid="post-edit-btn">
                      <Edit3 size={12} className="mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-rose-600" onClick={removePost} data-testid="post-delete-btn">
                      <Trash2 size={12} className="mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {editing ? (
              <div className="space-y-3">
                <input
                  className="w-full px-3 py-2 border rounded-md text-lg font-semibold"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  data-testid="post-edit-title"
                />
                <MarkdownEditor value={editBody} onChange={setEditBody} rows={8} testIdPrefix="post-edit-body" />
                <div className="flex items-center gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => { setEditing(false); setEditTitle(post.title); setEditBody(post.body); }}>Cancel</Button>
                  <Button size="sm" onClick={savePostEdit} data-testid="post-edit-save-btn">Save</Button>
                </div>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-zinc-900 leading-snug" data-testid="post-title">{post.title}</h1>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <AuthorChip author={post.author} when={post.created_at} />
                  {post.view_count > 0 && <span className="text-xs text-zinc-400">· {post.view_count} view{post.view_count === 1 ? "" : "s"}</span>}
                </div>
                <div className="prose prose-sm max-w-none mt-4 text-zinc-800" data-testid="post-body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{post.body || ""}</ReactMarkdown>
                </div>
              </>
            )}
          </div>
        </div>
      </article>

      <div className="mt-6">
        <h2 className="text-base font-semibold text-zinc-900 mb-2">
          {post.reply_count} {post.reply_count === 1 ? "Reply" : "Replies"}
        </h2>
        <div className="bg-white border rounded-lg divide-y" data-testid="forum-reply-list">
          {childrenOf(null).length === 0 && (
            <div className="px-4 py-6 text-sm text-zinc-500 text-center" data-testid="forum-no-replies">
              No replies yet. Be the first to chime in.
            </div>
          )}
          {childrenOf(null).map((r) => (
            <div key={r.id} className="px-4">
              <ReplyItem
                reply={r}
                postId={post.id}
                currentUser={user}
                isPostAuthor={isOwner}
                isBestAnswer={post.best_reply_id === r.id}
                onChanged={load}
              >
                {childrenOf(r.id).map((child) => (
                  <ReplyItem
                    key={child.id}
                    reply={child}
                    postId={post.id}
                    currentUser={user}
                    isPostAuthor={isOwner}
                    isBestAnswer={post.best_reply_id === child.id}
                    onChanged={load}
                  />
                ))}
              </ReplyItem>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 bg-white border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-zinc-900">Your reply</h3>
        <ReplyComposer postId={post.id} onPosted={load} />
      </div>

      {/* Tiny inline report dialog */}
      {reportOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setReportOpen(false)} data-testid="report-dialog">
          <div className="bg-white rounded-lg max-w-md w-full p-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold mb-2">Report this post</h3>
            <p className="text-xs text-zinc-500 mb-3">Tell us why — we review every report.</p>
            <textarea
              rows={3}
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              className="w-full border rounded-md px-2 py-1 text-sm"
              placeholder="e.g. spam, off-topic deal listing, harassment…"
              data-testid="report-reason-input"
            />
            <div className="flex items-center gap-2 justify-end mt-3">
              <Button variant="outline" size="sm" onClick={() => setReportOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={submitReport} disabled={reportReason.trim().length < 3} data-testid="report-submit-btn">Submit</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
